// IndexedDB-backed store for passing File objects between routes — survives
// full-page navigation (e.g. Cashfree redirect → /payment/return → /studio).
//
// Fast in-memory path is still preferred when available; IDB is the persistence
// fallback. Same module instance is used by Landing (write), Studio (read),
// AuthGateModal (write before checkout), PaymentReturn (read after payment).

const DB_NAME = "cinelut_pending";
const DB_VERSION = 1;
const STORE = "uploads";
const KEY = "current";

// In-memory cache for the common in-tab navigation path.
let memCache = null; // { video, reference, prompt }

function openDB() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB not supported"));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function idbSet(value) {
  try {
    const db = await openDB();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).put(value, KEY);
      tx.oncomplete = () => {
        db.close();
        resolve(true);
      };
      tx.onerror = () => {
        db.close();
        reject(tx.error);
      };
    });
  } catch (_) {
    return false;
  }
}

async function idbGet() {
  try {
    const db = await openDB();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, "readonly");
      const req = tx.objectStore(STORE).get(KEY);
      req.onsuccess = () => {
        db.close();
        resolve(req.result || null);
      };
      req.onerror = () => {
        db.close();
        reject(req.error);
      };
    });
  } catch (_) {
    return null;
  }
}

async function idbDel() {
  try {
    const db = await openDB();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).delete(KEY);
      tx.oncomplete = () => {
        db.close();
        resolve(true);
      };
      tx.onerror = () => {
        db.close();
        reject(tx.error);
      };
    });
  } catch (_) {
    return false;
  }
}

/**
 * Persist pending uploads. Writes both to in-memory cache AND IndexedDB.
 * Returns a Promise that resolves once IDB write completes (use `await` before
 * navigating away if you need durability for a full-page redirect).
 */
export const setPendingUploads = async (video, reference, prompt = "") => {
  memCache = { video, reference, prompt: prompt || "" };
  if (video && reference) {
    await idbSet({ video, reference, prompt: prompt || "" });
  }
  return true;
};

/**
 * Read pending uploads. Tries in-memory first; falls back to IDB. Does NOT
 * clear them — call clearPendingUploads() explicitly after they're consumed.
 */
export const getPendingUploads = async () => {
  if (memCache && memCache.video && memCache.reference) {
    return { ...memCache };
  }
  const persisted = await idbGet();
  if (persisted && persisted.video && persisted.reference) {
    memCache = persisted;
    return { ...persisted };
  }
  return null;
};

/**
 * Read + clear in one shot (atomic from the caller's perspective). Returns
 * null if there are no pending uploads.
 */
export const consumePendingUploads = async () => {
  const out = await getPendingUploads();
  if (out) {
    memCache = null;
    await idbDel();
  }
  return out;
};

/** Synchronous quick-check used by render-path guards. Only sees in-memory.
 *  Use `hasPendingUploadsAsync()` for IDB-aware truth across navigation. */
export const hasPendingUploadsMem = () =>
  Boolean(memCache && memCache.video && memCache.reference);

/** IDB-aware check (async). */
export const hasPendingUploadsAsync = async () => {
  if (hasPendingUploadsMem()) return true;
  const v = await idbGet();
  return Boolean(v && v.video && v.reference);
};

export const clearPendingUploads = async () => {
  memCache = null;
  await idbDel();
};
